import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  MapPin, 
  Clock, 
  DollarSign, 
  Calendar, 
  Users, 
  Building,
  FileText,
  ArrowRight
} from 'lucide-react';

interface JobPosting {
  id: string;
  title: string;
  description: string;
  requirements: string;
  location: string;
  employment_type: string;
  salary_min: number | null;
  salary_max: number | null;
  hourly_rate_min: number | null;
  hourly_rate_max: number | null;
  benefits: string | null;
  schedule_requirements: string | null;
  experience_level: string;
  created_at: string;
  business_id: string;
}

interface Business {
  id: string;
  name: string;
  description: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
}

const JobDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jobPosting, setJobPosting] = useState<JobPosting | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  
  const qrCode = searchParams.get('code');

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!qrCode) {
        toast.error("Invalid job link.");
        navigate('/');
        return;
      }

      try {
        // Get the QR code record to find the business
        const { data: qrCodeData, error: qrError } = await supabase
          .from('qr_codes')
          .select('id, business_id, type')
          .eq('unique_url_path', qrCode)
          .eq('type', 'job-posting')
          .single();

        if (qrError || !qrCodeData) {
          toast.error("This QR code is not valid for job applications.");
          navigate('/');
          return;
        }

        // Fetch active job postings for this business
        const { data: jobPostings, error: jobError } = await supabase
          .from('job_postings')
          .select('*')
          .eq('business_id', qrCodeData.business_id)
          .eq('is_active', true)
          .or('expires_at.is.null,expires_at.gt.now()')
          .order('created_at', { ascending: false })
          .limit(1);

        if (jobError || !jobPostings || jobPostings.length === 0) {
          toast.error("This job posting is no longer available.");
          navigate('/');
          return;
        }

        // Fetch business information
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, description, website, phone, email')
          .eq('id', qrCodeData.business_id)
          .single();

        if (businessError || !businessData) {
          toast.error("Unable to load business information.");
          navigate('/');
          return;
        }

        setJobPosting(jobPostings[0]);
        setBusiness(businessData);
      } catch (error: any) {
        console.error('Error fetching job details:', error);
        toast.error("Failed to load job information.");
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [qrCode, navigate]);

  const formatSalary = () => {
    if (!jobPosting) return null;
    
    const { salary_min, salary_max, hourly_rate_min, hourly_rate_max } = jobPosting;
    
    if (salary_min || salary_max) {
      if (salary_min && salary_max) {
        return `$${salary_min.toLocaleString()} - $${salary_max.toLocaleString()} per year`;
      } else if (salary_min) {
        return `From $${salary_min.toLocaleString()} per year`;
      } else if (salary_max) {
        return `Up to $${salary_max.toLocaleString()} per year`;
      }
    }
    
    if (hourly_rate_min || hourly_rate_max) {
      if (hourly_rate_min && hourly_rate_max) {
        return `$${hourly_rate_min} - $${hourly_rate_max} per hour`;
      } else if (hourly_rate_min) {
        return `From $${hourly_rate_min} per hour`;
      } else if (hourly_rate_max) {
        return `Up to $${hourly_rate_max} per hour`;
      }
    }
    
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!jobPosting || !business) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="text-center py-8">
          <h1 className="text-3xl font-bold mb-2">{jobPosting.title}</h1>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Building className="h-4 w-4" />
            <span className="text-lg">{business.name}</span>
          </div>
        </div>

        {/* Quick Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">{jobPosting.location || 'Not specified'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <Badge variant="outline" className="font-medium">
                    {jobPosting.employment_type.replace('-', ' ')}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Experience</p>
                  <p className="font-medium">{jobPosting.experience_level.replace('-', ' ')}</p>
                </div>
              </div>
              
              {formatSalary() && (
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Compensation</p>
                    <p className="font-medium text-sm">{formatSalary()}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Job Description */}
        {jobPosting.description && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Job Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {jobPosting.description}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Requirements */}
        {jobPosting.requirements && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
              <CardTitle className="text-sm font-medium">Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {jobPosting.requirements}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Benefits */}
        {jobPosting.benefits && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
              <CardTitle className="text-sm font-medium">Benefits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {jobPosting.benefits}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedule Requirements */}
        {jobPosting.schedule_requirements && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
              <CardTitle className="text-sm font-medium">Schedule & Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {jobPosting.schedule_requirements}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Company Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">About {business.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {business.description && (
              <p className="text-sm leading-relaxed">{business.description}</p>
            )}
            
            <div className="flex flex-wrap gap-4 text-sm">
              {business.website && (
                <a 
                  href={business.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Visit Website
                </a>
              )}
              {business.phone && (
                <a 
                  href={`tel:${business.phone}`}
                  className="text-primary hover:underline"
                >
                  {business.phone}
                </a>
              )}
              {business.email && (
                <a 
                  href={`mailto:${business.email}`}
                  className="text-primary hover:underline"
                >
                  {business.email}
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Apply Section */}
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <h3 className="text-xl font-semibold">Ready to Apply?</h3>
              <p className="text-muted-foreground">
                Complete our application form to be considered for this position.
              </p>
              <Link to={`/job-application?code=${qrCode}`}>
                <Button size="lg" className="px-8">
                  Apply Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="text-center text-sm text-muted-foreground pb-8">
          <p>Job posted on {formatDate(jobPosting.created_at)}</p>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;